import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        setScrolled(window.scrollY > 40);
        ticking = false;
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Prevent body scroll while mobile drawer is open
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const navItems = [
    { label: "Work", href: "#work" },
    { label: "Services", href: "#services" },
    { label: "About", href: "#about" },
    { label: "Contact", href: "#contact" },
  ];

  const handleLinkClick = () => setOpen(false);

  return (
    <motion.nav
      data-testid="navbar"
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled || open ? "bg-[#050505]/95 md:bg-[#050505]/80 md:backdrop-blur-xl border-b border-[#222]" : "bg-transparent"
      }`}
    >
      <div className="flex items-center justify-between px-6 md:px-16 lg:px-32 py-5">
        <a href="#" data-testid="navbar-logo" onClick={handleLinkClick} className="font-display text-xl md:text-2xl font-bold tracking-tighter uppercase">
          Podcast<span className="text-[#F4C75E] ml-2">Studio</span>
        </a>

        <ul className="hidden lg:flex items-center gap-10">
          {navItems.map((item) => (
            <li key={item.label}>
              <a
                href={item.href}
                data-testid={`navbar-link-${item.label.toLowerCase()}`}
                className="text-sm uppercase tracking-[0.2em] text-[#A3A3A3] hover:text-[#F5F5F5] transition-colors duration-300"
              >
                {item.label}
              </a>
            </li>
          ))}
        </ul>

        <a
          href="https://calendly.com/darshan081200/30min"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="navbar-cta"
          className="hidden lg:inline-flex whitespace-nowrap items-center gap-2 border border-[#D4AF37] text-[#D4AF37] px-5 py-2.5 text-xs uppercase tracking-[0.2em] font-semibold hover:bg-[#D4AF37] hover:text-black transition-all duration-300"
        >
          Work With Us
          <span aria-hidden>→</span>
        </a>

        {/* Mobile toggle */}
        <button
          type="button"
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
          data-testid="navbar-mobile-toggle"
          onClick={() => setOpen((v) => !v)}
          className="lg:hidden inline-flex items-center justify-center w-10 h-10 rounded-md border border-[#2a2a2a] text-[#F4C75E] hover:border-[#F4C75E] transition-colors"
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {/* Mobile dropdown drawer */}
      <AnimatePresence>
        {open && (
          <motion.div
            key="mobile-drawer"
            data-testid="navbar-mobile-menu"
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
            className="lg:hidden border-t border-[#1a1a1a] bg-[#050505]/98 backdrop-blur-xl"
          >
            <ul className="flex flex-col px-6 py-4">
              {navItems.map((item, i) => (
                <li key={item.label} className={i > 0 ? "border-t border-[#141414]" : ""}>
                  <a
                    href={item.href}
                    onClick={handleLinkClick}
                    data-testid={`navbar-mobile-link-${item.label.toLowerCase()}`}
                    className="flex items-center justify-between py-4 text-sm uppercase tracking-[0.25em] text-[#E5E5E5] hover:text-[#F4C75E] transition-colors"
                  >
                    <span>{item.label}</span>
                    <span aria-hidden className="text-[#F4C75E]">→</span>
                  </a>
                </li>
              ))}
            </ul>
            <div className="px-6 pb-6">
              <a
                href="https://calendly.com/darshan081200/30min"
                target="_blank"
                rel="noopener noreferrer"
                onClick={handleLinkClick}
                data-testid="navbar-mobile-cta"
                className="flex items-center justify-center gap-2 w-full bg-[#F4C75E] text-black font-bold uppercase tracking-[0.2em] text-xs py-4 hover:bg-white transition-colors"
              >
                Work With Us
                <span aria-hidden>→</span>
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
}
